import React, { Fragment, useRef, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/auth';
import { Dialog, Transition } from '@headlessui/react';
import { DocumentPlusIcon, CheckIcon, ClockIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getJustificationsCompanyList } from '../services/api_justifications';
import { listOrganizationalUnits } from "../services/api_org_unit";
import ComboboxAutocomplete from './combobox_autocomplete';
import { createEmployeeJustificationRequest, deleteJustificationRequest } from '../services/api_justification_requests';
import Swal from 'sweetalert2';
import moment from 'moment';
import { getDayCalendarWorkingHour } from '../services/api_calendar';
import { BriefcaseIcon } from "@heroicons/react/24/outline";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import CustomTooltipComponent from './custom_tooltip_component';
import LoaderSvg from './loader_svg';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import QueryBuilderOutlinedIcon from '@mui/icons-material/QueryBuilderOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';


const AddGiustificativoModalAssignWH = ({ closeModalAddGiustificativi, dipendente, formattedDay, selectedUnit, onAfterSave }) => {
    const cancelButtonRef = useRef(null);
    const { token, profile } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingJR, setIsLoadingJR] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isErrorJR, setIsErrorJR] = useState(false);
    const [error, setError] = useState('');
    const [isLoadingOu, setIsLoadingOU] = useState(false);
    const [giustificativi, setGiustificativi] = useState([]);
    const [giustificativoSelected, setGiustificativoSelected] = useState(null);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [startDay, setStartDay] = useState(formatDate(formattedDay));
    const [endDay, setEndDay] = useState(formatDate(formattedDay));
    const [note, setNote] = useState('');
    const [timeError, setTimeError] = useState('');
    const [isInputReset, setIsInputReset] = useState(false);
    const [units, setUnits] = useState([]);
    const [tempSelectedUnit, setTempSelectedUnit] = useState(null);
    const [employeeDayDetails, setEmployeeDayDetails] = useState(null);
    const [isForbidden, setIsForbidden] = useState(false);
    const controllerRef = useRef(null);

    const hasManageJustificationPermission =
        profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions?.includes('hr.manage_justification_all');

    const hasViewTimbraturePermission =
        profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.view_inouts_all') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_all');

    const hasViewShiftsPermission =
        profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.view_shifts_all') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_all');

    const isEmployee = profile.customer_type === 'employee';
    
    useEffect(() => {
        if (selectedUnit) {
            setTempSelectedUnit(selectedUnit);
        }
    }, [selectedUnit]);

    function formatDate(dateString) {
        const date = moment(dateString, 'YYYY-MM-DD', true);
        return date.isValid() ? date.format('DD/MM/YYYY') : '';
    }

    const fetchEmployeeDayDetails = async () => {
        if (dipendente?.id && formattedDay && token) {
            const employeeId = dipendente.id;
            
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            const controller = new AbortController();
            controllerRef.current = controller;
            const signal = controller.signal;

            try {
                setIsLoading(true);
                setIsError(false);
                setIsForbidden(false);

                const calendarResponse = await getDayCalendarWorkingHour(
                    token,
                    formattedDay,
                    null,
                    [employeeId],
                    1, 
                    10,
                    signal
                );

                const modifiedCalendarResponse = calendarResponse.results.map((entry) => ({
                    ...entry,
                    days: entry.days.map((day) => ({
                        ...day,
                        justification_requests:
                            isEmployee && !hasManageJustificationPermission
                                ? []
                                : day.justification_requests,
                        inouts: isEmployee && !hasViewTimbraturePermission ? [] : day.inouts,
                        shifts: day.shifts.map((shift) => ({
                            ...shift,
                            time_slots:
                                isEmployee && !hasViewShiftsPermission ? [] : shift.time_slots,
                        })),
                    })),
                }));

                const employeeDayDetails = modifiedCalendarResponse.find(employee => employee.id === employeeId);
                const dayDetails = employeeDayDetails?.days.find(day => day.date === formattedDay);

                setEmployeeDayDetails(dayDetails);
                setIsLoading(false);
                return dayDetails;
            } catch (error) {
                if (error?.name != 'CanceledError') {
                    console.error(error);
                    setIsLoading(false);
                }

                if (error.response && error.response.status === 403) {
                    setIsForbidden(true);
                } else {
                    setIsError(true);
                }
            }
        }
    };

    useEffect(() => {
        fetchEmployeeDayDetails();
    }, [dipendente?.id, formattedDay, token, profile.employee?.permissions]);

    useEffect(() => {
        setIsLoadingJR(true);
        setIsErrorJR(false);

        const fetchData = async () => {
            try {
                const giustificativi = await getJustificationsCompanyList(token);
                setGiustificativi(giustificativi);
                setIsLoadingJR(false);
            } catch (error) {
                setIsLoadingJR(false);
                console.error('Error fetching justifications:', error);
                setIsErrorJR(true);
            }
        };

        fetchData();
    }, [token]);

    useEffect(() => {

    }, [giustificativoSelected]);

    useEffect(() => {
        async function fetchData() {
            try {
                setIsLoadingOU(true);
                const unitsData = await listOrganizationalUnits(token, true);
                setUnits(unitsData.data);
                setIsLoadingOU(false);
            } catch (error) {
                console.error('Error fetching units:', error);
                setError(true);
                setIsLoadingOU(false);
            }
        }
        fetchData();
    }, [token]);

    const handleSelectUnit = (selected) => {
        setTempSelectedUnit(selected);
    };

    const handleWorkTimeBlur = (field) => {
        let formattedTime = field === 'start' ? startTime : endTime;
        formattedTime = formattedTime.replace(/\D/g, '');

        while (formattedTime.length < 4) {
            formattedTime += '0';
        }

        formattedTime = formattedTime.slice(0, 4);
        const hours = formattedTime.slice(0, 2);
        const minutes = formattedTime.slice(2);
        formattedTime = `${hours}:${minutes}`;

        const isValidTime = /^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(formattedTime);

        if (!isValidTime) {
            if (field === 'start') {
                setStartTime('');
            } else {
                setEndTime('');
            }
            return;
        }

        if (field === 'start') {
            setStartTime(formattedTime);
        } else {
            setEndTime(formattedTime);
        }
    };

    const handleStartTimeChange = (e) => {
        let inputValue = e.target.value;
        inputValue = inputValue.replace(/\D/g, '');
        inputValue = inputValue.slice(0, 4);
        let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");

        if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
            formattedTime = formattedTime.replace(":", "");
        }

        setStartTime(formattedTime);

        if (endTime && formattedTime >= endTime) {
            setTimeError("L'orario di inizio deve essere precedente all'orario di fine.");
        } else {
            setTimeError('');
        }
    };

    const handleEndTimeChange = (e) => {
        let inputValue = e.target.value;
        inputValue = inputValue.replace(/\D/g, '');
        inputValue = inputValue.slice(0, 4);
        let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");

        if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
            formattedTime = formattedTime.replace(":", "");
        }

        setEndTime(formattedTime);

        if (startTime && formattedTime <= startTime) {
            setTimeError("L'orario di fine deve essere successivo all'orario di inizio.");
        } else {
            setTimeError('');
        }
    };

    
    const convertToISOFormat = (dateString) => {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month}-${day}`;
    };
    
    const handleGiustificativoChange = (giustificativo) => {
        setGiustificativoSelected(giustificativo);
    
        if (!giustificativo?.has_hours) {
            setStartTime('00:00');
            setEndTime('24:00');
        } else {
            setStartTime('');
            setEndTime('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (!startTime || !endTime || giustificativoSelected === null) {
            setTimeError("Compila tutti i campi obbligatori.");
            return;
        }

        if (!startDay || !endDay || !startTime || !endTime) {
            setTimeError("Data e orari devono essere specificati.");
            return;
        }

        let datetimeTo = convertToISOFormat(endDay);
        if (endTime === '24:00') {
            datetimeTo = moment(datetimeTo).add(1, 'days').format('YYYY-MM-DD')+'T00:00:00';
        } else {
            datetimeTo = `${datetimeTo}T${endTime}:00`;
        }
        
        const datetimeFrom = convertToISOFormat(startDay) + `T${startTime}:00`;
    
        const data = {
            datetime_from: datetimeFrom,
            datetime_to: datetimeTo,
            note,
            justification: giustificativoSelected ? giustificativoSelected.id : null,
            ou: tempSelectedUnit ? tempSelectedUnit.id : null,
        };

        const loader = Swal.fire({
            title: 'Salvataggio in corso...',
            html: 'Attendere',
            didOpen: () => {
                Swal.showLoading();
            },
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
        });
        
        try {
            await createEmployeeJustificationRequest(token, dipendente.id, data);

            const updatedDayDetails = await fetchEmployeeDayDetails();
            const hasJustifications = !!(updatedDayDetails?.justification_requests?.length > 0);
            onAfterSave?.(formattedDay, hasJustifications);
            
            setGiustificativoSelected(null);
            setStartTime('');
            setEndTime('');
            setNote('');
            setTimeError('');

            await Swal.fire({
                icon: 'success',
                title: 'Salvato!',
                text: 'La richiesta di giustificativo è stata inserita',
                timer: 1000,
                showConfirmButton: false,
            });

            loader.close();
        } catch (error) {
            console.error('Failed to update justification:', error);
            loader.close();
            let errorMessage = 'Si è verificato un errore durante la modifica del giustificativo. Per favore riprova.';
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = [];
    
                Object.keys(errorData).forEach(key => {
                    const messageArray = errorData[key];
                    if (Array.isArray(messageArray)) {
                        errorMessages.push(...messageArray);
                    }
                });
    
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(' ');
                }
            }
    
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
                confirmButtonText: 'OK',
            });
        } finally {
            loader.close();
        }
    };

    const handleDeleteRequest = async (justificationId) => {
        const confirmDelete = await Swal.fire({
            icon: 'warning',
            title: 'Conferma eliminazione',
            text: 'Stai eliminando un giustificativo. L\'azione non è reversibile. Confermi di voler eliminare?',
            showCancelButton: true,
            confirmButtonText: 'Conferma',
            cancelButtonText: 'Annulla',
            cancelButtonColor: '#d1d5db',
        });

        if (!confirmDelete.isConfirmed) return;

        setIsLoading(true);
        setIsError(false);

        try {
            await deleteJustificationRequest(token, justificationId);

            const updatedDayDetails = await fetchEmployeeDayDetails();
            const hasJustifications = !!(updatedDayDetails?.justification_requests?.length > 0);
            onAfterSave?.(formattedDay, hasJustifications);

            Swal.fire({
                icon: 'success',
                title: 'Eliminato!',
                text: 'La richiesta è stata eliminata con successo.',
                showConfirmButton: false,
                timer: 1000,
            });
        } catch (error) {
            console.error('Errore durante eliminazione:', error);
            setIsError(true);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Errore durante l\'eliminazione del giustificativo. Riprova.',
            });
        }
    };

    const formatTimeRange = (from, to) => {
        if (!from || !to) return '';

        const fromDate = moment(from);
        const toDate = moment(to);

        let toTimeFormatted = toDate.format('HH:mm');

        const isNextDayMidnight =
            toDate.format('HH:mm') === '00:00' &&
            toDate.isSame(fromDate.clone().add(1, 'day'), 'day');

        if (isNextDayMidnight) {
            toTimeFormatted = '24:00';
        }

        return `${fromDate.format('HH:mm')} - ${toTimeFormatted}`;
    };
    

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 overflow-y-auto top-10" style={{ zIndex: '150' }} onClose={() => {}} initialFocus={cancelButtonRef}>
                <div className="flex items-start justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="sticky top-0 inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-[60%] max-h-[90vh]">
                            <div className="sticky top-0 z-20 border-b border-gray-200 text-center flex items-center justify-center bg-indigo-200 py-3">
                                <DocumentPlusIcon className="h-5 w-5 text-indigo-700" />
                                <span className='w-4'></span>
                                <Dialog.Title as="h3" className="text-lg font-semibold text-indigo-700">
                                Aggiungi Giustificativo
                                </Dialog.Title>
                            </div>

                            <div className="overflow-y-auto max-h-[80vh] px-6 py-4">
                                <div className="col-span-6 flex justify-center shadow-md">
                                    <table className="w-full table-fixed border border-gray-200 shadow text-md">
                                        <thead className="bg-indigo-50 text-center">
                                            <tr>
                                                <th colSpan="3" className="px-4 py-2 text-indigo-700 text-sm font-bold text-center rounded-t-lg">
                                                    TURNO
                                                </th>
                                            </tr>
                                            <tr>
                                                <th className="px-4 pb-2 pt-0 text-gray-500 text-sm w-1/3">
                                                    Stato
                                                </th>
                                                <th className="px-4 pb-2 pt-0 text-gray-500 text-sm w-1/3">
                                                    <BriefcaseIcon className="w-4 h-4 text-indigo-700 me-1 inline-block" /> Fascia Oraria
                                                </th>
                                                <th className="px-4 pb-2 pt-0 text-gray-500 text-sm w-1/3">
                                                    <LocalBarIcon style={{ height: '15px', width: '15px', color: '#4f46e5', marginRight: '4px' }} /> Pausa
                                                </th>
                                            </tr>
                                        </thead>
                                        {isLoading ? (
                                            <tbody>
                                                <tr>
                                                    <td colSpan="3" className="py-2 h-4 text-center">
                                                        <LoaderSvg padding={4} svgColor={'fill-indigo-800'} />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        ) : (
                                            <tbody>
                                            {/* TURNI */}
                                            {employeeDayDetails?.shifts?.length > 0 &&
                                            employeeDayDetails.shifts.some(shift => shift.time_slots.length > 0) ? (
                                                employeeDayDetails.shifts.map((shift, shiftIndex) =>
                                                shift.time_slots.map((slot, slotIndex) => (
                                                    <tr key={`shift-${shiftIndex}-slot-${slotIndex}`} className="border-b">
                                                    <td className="border px-4 py-2 text-center align-middle font-semibold text-sm">
                                                        {shift.status === false ? (
                                                        <CustomTooltipComponent title="Turno da confermare" placement="top-start">
                                                            <>
                                                                <WorkHistoryOutlinedIcon style={{ height: '15px', width: '15px', color: '#f97316', marginRight: '4px' }} />
                                                                <span className="font-normal text-sm ml-2">Da confermare</span>
                                                            </>
                                                        </CustomTooltipComponent>
                                                        ) : (
                                                        <CustomTooltipComponent title="Turno confermato" placement="top-start">
                                                            <>
                                                                <CheckCircleOutlineOutlinedIcon style={{ height: '15px', width: '15px', color: '#22C55E', marginRight: '4px' }} />
                                                                <span className="font-normal text-sm ml-2">Confermato</span>
                                                            </>
                                                        </CustomTooltipComponent>
                                                        )}
                                                    </td>
                                                    <td className="border px-4 py-2 text-center align-middle font-semibold text-sm">
                                                        {slot.start_at} - {slot.end_at}
                                                    </td>
                                                    <td className="border px-4 py-2 text-center align-middle font-semibold text-sm">
                                                        {slot.breaks?.length > 0 ? (
                                                        <div className="space-y-1">
                                                            {slot.breaks.map((br, brIndex) => (
                                                            <div key={brIndex}>{br.start_at} - {br.end_at}</div>
                                                            ))}
                                                        </div>
                                                        ) : (
                                                        <span className="text-gray-400">Nessuna pausa</span>
                                                        )}
                                                    </td>
                                                    </tr>
                                                ))
                                                )
                                            ) : (
                                                <tr>
                                                    <td colSpan={3} className="text-center text-sm text-gray-500 py-4">
                                                        Non sono presenti turni
                                                    </td>
                                                </tr>
                                            )}

                                            {/* GIUSTIFICATIVI - Solo se presenti */}
                                            {!isLoading && employeeDayDetails?.justification_requests?.length > 0 && (
                                                <>
                                                    <tr className="bg-indigo-50">
                                                        <th colSpan="3" className="px-4 py-2 text-indigo-700 text-sm font-bold text-center">
                                                        GIUSTIFICATIVI
                                                        </th>
                                                    </tr>
                                                    <tr className="bg-indigo-50 text-center">
                                                        <th className="px-4 pb-2 pt-0 text-gray-500 text-sm w-1/3">Stato</th>
                                                        <th className="px-4 pb-2 pt-0 text-gray-500 text-sm w-1/3">
                                                            <QueryBuilderOutlinedIcon style={{ height: '15px', width: '15px', color: '#4f46e5', marginRight: '4px' }} /> Fascia Oraria
                                                        </th>
                                                        <th className="px-4 pb-2 pt-0 text-gray-500 text-sm w-1/3">
                                                            <TextSnippetOutlinedIcon style={{ height: '15px', width: '15px', color: '#4f46e5', marginRight: '4px' }} /> Tipologia
                                                        </th>
                                                    </tr>

                                                    {employeeDayDetails.justification_requests.map((j, jIndex) => (
                                                        <tr key={`just-${jIndex}`} className="border-b bg-white">
                                                            <td className="border px-4 py-2 text-center align-middle font-semibold text-sm">
                                                                {j.status === 'approved' ? (
                                                                    <CustomTooltipComponent title="Giustificativo approvato" placement="top-start">
                                                                        <>
                                                                            <CheckCircleOutlineOutlinedIcon style={{ height: '15px', width: '15px', color: '#22C55E', marginRight: '4px' }} />
                                                                            <span className="font-normal text-sm ml-2">Approvato</span>
                                                                        </>
                                                                    </CustomTooltipComponent>
                                                                ) : (
                                                                    <CustomTooltipComponent title="In attesa di approvazione" placement="top-start">
                                                                        <>
                                                                            <PendingActionsOutlinedIcon style={{ height: '15px', width: '15px', color: '#f97316', marginRight: '4px' }} />
                                                                            <span className="font-normal text-sm ml-2">In attesa</span>
                                                                        </>
                                                                    </CustomTooltipComponent>
                                                                )}
                                                            </td>
                                                            <td className="border px-4 py-2 text-center align-middle font-semibold text-sm">
                                                                {formatTimeRange(j.datetime_from, j.datetime_to)}
                                                            </td>
                                                            <td className="px-4 py-2 text-center align-center justify-center gap-4 flex font-semibold text-sm text-gray-500"> {j.description} 
                                                                <span className='align-center'>
                                                                    <CustomTooltipComponent title="Elimina giustificativo" arrow>
                                                                        <button
                                                                            onClick={() => handleDeleteRequest(j.id)}
                                                                            className="text-indigo-700 hover:text-red-700 text-sm font-bold"
                                                                        >
                                                                            <TrashIcon className='h-4 w-4'  />
                                                                        </button>
                                                                    </CustomTooltipComponent>
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </>
                                            )}
                                            </tbody>
                                        )}
                                    </table>
                                </div>

                                <form onSubmit={handleSubmit} className='py-4'>
                                    <div className='grid grid-cols-3 gap-3 w-full p-2 align-middle'>
                                        <div className='col-span-1 flex items-center font-semibold text-sm text-gray-600 uppercase'>
                                            Dipendente
                                        </div>
                                        <div className='col-span-2 text-sm text-gray-600 flex-col items-center'>
                                            {dipendente?.last_name && dipendente?.first_name ? `${dipendente?.last_name} ${dipendente?.first_name}` : dipendente?.cf}
                                        </div>

                                        <div className='col-span-1 flex items-center font-semibold text-sm text-gray-600 uppercase'>
                                            Giustificativo *
                                        </div>
                                        {isLoadingJR ? (
                                            <div className='col-span-2'>Caricamento...</div>
                                        ) : (
                                            <div className='col-span-2 items-center border border-gray-200 rounded-md'>
                                                {employeeDayDetails?.shifts && employeeDayDetails.shifts.length > 0 ? (
                                                    <ComboboxAutocomplete
                                                        options={giustificativi.map((giustificativo) => ({
                                                            id: giustificativo.id,
                                                            name: giustificativo.justification.description,
                                                            value: giustificativo.id,
                                                            has_hours: giustificativo?.justification?.has_hours
                                                        }))}
                                                        bgActiveColor="bg-indigo-100"
                                                        textActiveColor="text-gray-900"
                                                        placeholder='Seleziona il giustificativo...'
                                                        onChange={(selectedOption) => {
                                                            setGiustificativoSelected(selectedOption ?? null);
                                                            handleGiustificativoChange(selectedOption);
                                                        }}
                                                        value={giustificativoSelected}
                                                        displayValue={(giustificativoSelected) => {
                                                            const selectedOption = giustificativi.find(
                                                            (giustificativo) => giustificativo.id === giustificativoSelected?.id
                                                            );
                                                            return selectedOption ? selectedOption.justification.description : '';
                                                        }}
                                                        positionOptions='absolute'
                                                        required
                                                    />
                                                ) : (
                                                    <ComboboxAutocomplete
                                                        options={giustificativi
                                                            .filter(giustificativo => giustificativo.justification.strategy === 'increase')
                                                            .map((giustificativo) => ({
                                                                id: giustificativo.id,
                                                                name: giustificativo.justification.description,
                                                                value: giustificativo.id,
                                                                has_hours: giustificativo?.justification?.has_hours
                                                            }))
                                                        }
                                                        bgActiveColor="bg-indigo-100"
                                                        textActiveColor="text-gray-900"
                                                        placeholder='Seleziona il giustificativo...'
                                                        onChange={(selectedOption) => {
                                                            setGiustificativoSelected(selectedOption ?? null);
                                                            handleGiustificativoChange(selectedOption);
                                                        }}
                                                        value={giustificativoSelected}
                                                        displayValue={(giustificativoSelected) => {
                                                            const selectedOption = giustificativi.find(
                                                            (giustificativo) => giustificativo.id === giustificativoSelected?.id
                                                            );
                                                            return selectedOption ? selectedOption.justification.description : '';
                                                        }}
                                                        positionOptions='absolute'
                                                    />
                                                )}
                                            </div>
                                        )}
                                        
                                        <div className='col-span-1 flex items-center font-semibold text-sm text-gray-600 uppercase'>
                                            Giorno selezionato *
                                        </div>
                                        <div className='col-span-2 text-sm items-center w-full'>
                                            <div className="text-sm font-normal items-center" style={{ position: 'relative' }}>
                                                <input
                                                    name="selectedDay"
                                                    type="text"
                                                    value={startDay}
                                                    readOnly
                                                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md block w-full shadow-md sm:text-sm bg-indigo-50 text-gray-700 font-semibold cursor-not-allowed"
                                                />
                                            </div>
                                        </div>
                                
                                        {giustificativoSelected && giustificativoSelected?.has_hours && (
                                            <>
                                                <div className='col-span-1 flex items-center font-semibold text-sm text-gray-600 uppercase'>
                                                    Dalle ore *
                                                </div>
                                                <div className="col-span-2 text-sm items-center" style={{ position: 'relative' }}>
                                                    <input 
                                                        type="text"
                                                        placeholder='hh:mm' 
                                                        value={startTime} 
                                                        onInput={handleStartTimeChange}
                                                        onBlur={() => handleWorkTimeBlur('start')}
                                                        className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-md sm:text-sm required" 
                                                        style={{ paddingRight: '2.5rem' }}
                                                        required
                                                    />
                                                    <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                                    <ClockIcon className='w-4 h-4' />
                                                    </span>
                                                </div>      
                                                <div className='col-span-1 flex items-center font-semibold text-sm text-gray-600 uppercase'>
                                                    Alle ore *
                                                </div>
                                                <div className='col-span-2 text-sm items-center' style={{ position: 'relative' }}>
                                                    <input 
                                                        type="text"
                                                        placeholder='hh:mm' 
                                                        value={endTime} 
                                                        onInput={handleEndTimeChange} 
                                                        onBlur={() => handleWorkTimeBlur('end')}
                                                        className="t-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-md sm:text-sm required" 
                                                        style={{ paddingRight: '2.5rem' }}
                                                        required
                                                    />
                                                    <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                                                        <ClockIcon className='w-4 h-4' />
                                                    </span>
                                                </div>
                                            </>
                                        )}

                                        <div className='col-span-1 items-center font-semibold text-sm text-gray-600 uppercase pt-1'>
                                            Note
                                        </div>
                                        <textarea 
                                            className='col-span-2 h-20 text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-200 shadow-md rounded-md border border-gray-200 rounded-md'
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                        />
                                        <div className='col-span-1 flex items-center font-semibold text-sm text-gray-600 uppercase'>
                                            Associa Unità
                                        </div>
                                        {isLoadingOu ? (
                                            <div className='col-span-2'>Caricamento...</div>
                                        ) : (
                                            <div className='col-span-2 items-center border border-gray-200 rounded-md'>
                                                <ComboboxAutocomplete
                                                    options={units}
                                                    bgActiveColor="bg-indigo-100"
                                                    textActiveColor="text-gray-900"
                                                    placeholder="Seleziona l'Unità Organizzativa"
                                                    onChange={handleSelectUnit}
                                                    value={tempSelectedUnit}
                                                    positionOptions='relative'
                                                />
                                            </div>
                                        )}
                                    </div>
                                </form>

                                {timeError && <div className='justify-center flex text-xs text-red-500 pt-2'>Compila tutti i campi obbligatori</div> }

                                <div className="flex justify-center gap-4 py-2">
                                    <button
                                        type="button"
                                        className="py-2 bg-indigo-600 rounded-md  w-28 text-white hover:bg-indigo-500 font-semibold text-sm uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                        onClick={handleSubmit}
                                        ref={cancelButtonRef}
                                    >   
                                     
                                        SALVA
                                    </button>

                                    <button
                                        type="button"
                                        className="uppercase w-28 bg-white border border-gray-200 shadow-md text-gray-700 text-sm font-semibold px-4 py-2 rounded-md ml-4 hover:bg-gray-300 focus:outline-none focus:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                        onClick={closeModalAddGiustificativi}
                                    >
                                        CHIUDI
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default AddGiustificativoModalAssignWH;
